<template>
  <v-dialog v-model="visible" persistent max-width="520">
    <v-card>
      <v-card-title class="primary px-4">
        <div class="d-flex align-center">
          <v-btn
            v-if="!mandatory"
            color="white"
            icon
            :disabled="loading"
            @click="cancel"
            ><v-icon size="20px">mdi-close</v-icon></v-btn
          >
          <span
            class="white--text"
            :class="{ 'pl-2': mandatory }"
            style="font-size: 16px !important; font-weight: 400"
            >Xác thực 2 bước</span
          >
        </div>
      </v-card-title>
      <v-card-text class="pt-4 black--text">
        <p v-if="step === 'loading'" class="mb-3">Đang gửi mã xác thực...</p>

        <MfaTelegramEnrollStep v-if="step === 'enroll'" :link="telegramLink" />

        <div v-if="step === 'otp'">
          <p class="mb-2">
            Mã xác thực 6 số đã được gửi tới Telegram
            <strong>{{ maskedTelegram }}</strong> qua bot của hệ thống. Mở
            Telegram, xem tin nhắn mới nhất của bot và nhập mã vào ô bên dưới
            (mã có hiệu lực 5 phút).
          </p>
          <v-text-field
            v-model="code"
            label="Mã xác thực"
            outlined
            dense
            maxlength="6"
            inputmode="numeric"
            autocomplete="one-time-code"
            autofocus
            :disabled="loading"
            @keyup.enter="verify"
          ></v-text-field>
          <p
            v-if="resendIn > 0"
            class="neutral--text mb-0"
            style="font-size: 13px"
          >
            Chưa nhận được mã? Gửi lại sau {{ resendIn }} giây.
          </p>
        </div>

        <p v-if="errorMsg" class="semantic_error--text mt-2 mb-0">
          {{ errorMsg }}
        </p>
        <p
          v-if="step === 'blocked' && resendIn > 0"
          class="neutral--text mt-1 mb-0"
          style="font-size: 13px"
        >
          Thử lại sau {{ formatSeconds(resendIn) }}.
        </p>
      </v-card-text>
      <v-card-actions class="px-4 pb-4">
        <v-btn
          v-if="step === 'otp' || step === 'blocked'"
          text
          color="primary"
          :disabled="loading || resendIn > 0"
          @click="challengeId ? resend() : sendChallenge()"
        >
          {{ challengeId ? "Gửi lại mã" : "Gửi mã mới" }}
        </v-btn>
        <v-btn
          v-if="step === 'enroll'"
          text
          color="primary"
          :disabled="loading"
          @click="sendChallenge"
        >
          Lấy link mới
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="neutral_color_sub4"
          class="rounded-lg"
          depressed
          :disabled="loading"
          @click="mandatory ? logout() : cancel()"
        >
          {{ mandatory ? "Đăng xuất" : "Hủy" }}
        </v-btn>
        <v-btn
          v-if="step === 'otp'"
          color="primary"
          class="rounded-lg"
          depressed
          :loading="loading"
          :disabled="!/^\d{6}$/.test(code)"
          @click="verify"
        >
          Xác nhận
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from "vuex"
import api from "@/services"
import { completeMfaStepUp, cancelMfaStepUp } from "@/helpers/mfaStepUp.js"
import MfaTelegramEnrollStep from "./MfaTelegramEnrollStep.vue"

const RESEND_COOLDOWN = 60
const ENROLL_POLL_MS = 3000

export default {
  components: { MfaTelegramEnrollStep },
  data() {
    return {
      visible: false,
      loading: false,
      // loading | enroll (chưa gắn Telegram) | otp | blocked (lỗi, chờ thử lại)
      step: "loading",
      challengeId: null,
      maskedTelegram: "",
      telegramLink: "",
      code: "",
      errorMsg: "",
      resendIn: 0,
      cooldownTimer: null,
      pollTimer: null,
    }
  },
  computed: {
    ...mapState({
      show: (state) => state.showMfaStepUp,
      mandatory: (state) => state.mfaStepUpMandatory,
    }),
  },
  watch: {
    show(val) {
      this.visible = val
      if (val) {
        this.reset()
        this.sendChallenge()
      } else {
        this.stopTimers()
      }
    },
  },
  beforeDestroy() {
    this.stopTimers()
  },
  methods: {
    reset() {
      this.step = "loading"
      this.challengeId = null
      this.maskedTelegram = ""
      this.telegramLink = ""
      this.code = ""
      this.errorMsg = ""
      this.resendIn = 0
    },
    startCooldown(seconds) {
      clearInterval(this.cooldownTimer)
      this.resendIn = seconds
      this.cooldownTimer = setInterval(() => {
        this.resendIn -= 1
        if (this.resendIn <= 0) {
          clearInterval(this.cooldownTimer)
        }
      }, 1000)
    },
    stopTimers() {
      clearInterval(this.cooldownTimer)
      clearInterval(this.pollTimer)
      this.pollTimer = null
    },
    // Chưa gắn Telegram: hỏi trạng thái định kỳ, gắn xong thì tự gửi OTP.
    startEnrollPolling() {
      clearInterval(this.pollTimer)
      this.pollTimer = setInterval(async () => {
        const res = await api.twoFactor.getMyTelegramStatus()
        const data = res && res.status === 200 && res.data.data
        if (
          data &&
          data.two_factor_method === "telegram" &&
          data.two_factor_enabled_at
        ) {
          clearInterval(this.pollTimer)
          this.pollTimer = null
          this.$store.commit(
            "toast/getSuccess",
            "Đã gắn Telegram, đang gửi mã xác thực",
          )
          this.sendChallenge()
        }
      }, ENROLL_POLL_MS)
    },
    // Lấy message của backend (sai mã kèm "Bạn còn N lần thử lại"); 429 kèm thời gian
    // chờ ở data.retry_after (ưu tiên, header Retry-After có thể bị CORS che)
    handleError(res) {
      const data = (res && res.data) || {}
      const code = data.error_code
      this.errorMsg = data.message || "Có lỗi xảy ra, vui lòng thử lại."
      if (res && res.status === 429) {
        const wait =
          parseInt(data.data && data.data.retry_after, 10) ||
          parseInt(res.headers && res.headers["retry-after"], 10)
        if (wait > 0) {
          this.startCooldown(wait)
        }
      }
      // Tài khoản bị khoá OTP (TTCN-1616) / mã hết hạn: bỏ challenge cũ, chờ rồi xin mã mới
      if (code === "otp_locked_out" || code === "otp_expired") {
        this.challengeId = null
        this.code = ""
        this.step = "blocked"
      }
      return code
    },
    async sendChallenge() {
      this.loading = true
      this.errorMsg = ""
      if (this.step !== "enroll") {
        this.step = "loading"
      }
      const res = await api.twoFactor.requestMfaChallenge()
      this.loading = false
      if (res && res.status === 202) {
        clearInterval(this.pollTimer)
        this.challengeId = res.data.data.challenge_id
        this.maskedTelegram = res.data.data.telegram_masked
        this.step = "otp"
        this.startCooldown(RESEND_COOLDOWN)
        return
      }
      const data = (res && res.data) || {}
      if (
        data.error_code === "two_factor_not_enrolled" &&
        data.data &&
        data.data.telegram_link
      ) {
        this.telegramLink = data.data.telegram_link
        this.step = "enroll"
        this.startEnrollPolling()
        return
      }
      this.step = "blocked"
      const code = this.handleError(res)
      if (code === "two_factor_not_enrolled") {
        // Phòng hờ backend không trả link gắn Telegram
        this.errorMsg =
          "Tài khoản chưa gắn Telegram và không lấy được link gắn. Vui lòng thử lại hoặc liên hệ quản trị hệ thống."
      }
      if (code === "mfa_not_required") {
        // Token không cần OTP (vd 2FA đang tắt): đóng dialog
        cancelMfaStepUp()
      }
    },
    async resend() {
      this.loading = true
      this.errorMsg = ""
      const res = await api.twoFactor.resendMfaCode(this.challengeId)
      this.loading = false
      if (res && res.status === 202) {
        this.code = ""
        this.startCooldown(RESEND_COOLDOWN)
        return
      }
      this.handleError(res)
    },
    async verify() {
      if (!this.challengeId || !/^\d{6}$/.test(this.code)) {
        return
      }
      this.loading = true
      this.errorMsg = ""
      const res = await api.twoFactor.verifyMfaCode(this.challengeId, this.code)
      this.loading = false
      if (res && res.status === 200 && res.data.data && res.data.data.token) {
        this.$store.commit("toast/getSuccess", "Xác thực 2 bước thành công")
        completeMfaStepUp(res.data.data.token)
        return
      }
      this.handleError(res)
    },
    formatSeconds(sec) {
      const m = Math.floor(sec / 60)
      const s = String(sec % 60).padStart(2, "0")
      return `${m}:${s}`
    },
    cancel() {
      cancelMfaStepUp()
    },
    logout() {
      // Mở hộp xác nhận đăng xuất có sẵn; huỷ thì quay lại đây (vẫn chờ Start bot)
      this.$store.commit("SET_LOGOUT_POPUP", true)
    },
  },
}
</script>
